import type { ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@musa.tech/design-system";
import { fmtFullNumber, isAbbreviated } from "@/utils/formatCompactNumber";

interface CompactValueProps {
  /**
   * Valor final. Decide se o tooltip aparece e é o que ele mostra — durante a
   * contagem o texto exibido está a caminho deste número, e o tooltip tem que
   * mostrar o real, não o do frame.
   */
  exact: number;
  /** Sufixo no valor exato do tooltip, ex.: "kg", "L", "itens". */
  unit?: string;
  /** Texto já formatado. Cada chamador formata, porque peso tem regra própria. */
  children: ReactNode;
  className?: string;
}

/**
 * Envolve um número abreviado com o valor exato no tooltip.
 *
 * O tooltip só aparece quando o número foi de fato abreviado — abreviar
 * destrói precisão, e num dashboard que o cliente confere contra a fatura o
 * valor cheio precisa estar a um hover de distância. Abaixo do limite o número
 * já sai inteiro, então um tooltip ali seria só ruído.
 */
export const CompactValue = ({
  exact,
  unit,
  children,
  className,
}: CompactValueProps) => {
  if (!isAbbreviated(exact)) return <>{children}</>;

  const exactLabel = unit
    ? `${fmtFullNumber(exact)} ${unit}`
    : fmtFullNumber(exact);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={cn("cursor-help", className)}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="text-xs leading-relaxed">
          {exactLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
